import {
  StarIcon,
  ReceiptPoundSterlingIcon,
  LayersIcon,
  BanIcon,
  BadgePercentIcon,
  Sparkle,
  SparkleIcon,
  LeafIcon,
} from 'lucide-react';
import React from 'react';
import { type components } from '~/fecom-api/schema';
import { cn } from '~/lib/utils';

export type ProductTagsProps = {
  variant: 'pdp' | 'basket';
  roundels?: components['schemas']['Roundel'][];
  minOrderQuantity?: number;
  isContractPrice?: boolean;
  isRestricted?: boolean;
  isDiscountExcluded?: boolean;
  isDiscountPrice?: boolean;
  limit?: number;
};

const priorityOrder = [
  'Your Price',
  'Your Contract Price',
  'Restricted Product',
  'Minimum Order Quantity',
  'Clearance',
  'Sale',
  'Reduced',
  'Buy More Save More',
  'Excluded from Discount',
  'Best Seller',
  'New',
  'Exclusive',
  'Eco Friendly',
];

export function ProductTags({
  variant,
  roundels,
  minOrderQuantity,
  isContractPrice,
  isDiscountPrice,
  isRestricted,
  limit,
  isDiscountExcluded,
}: ProductTagsProps) {
  const allBadges = new Map<string, ProductTagProps>();

  if (isDiscountPrice) {
    allBadges.set('Your Price', {
      text: 'Your Price',
      icon: <ReceiptPoundSterlingIcon />,
    });
  }

  if (isContractPrice) {
    allBadges.set('Your Contract Price', {
      text: 'Your Contract Price',
      icon: <ReceiptPoundSterlingIcon />,
    });
  }

  if (isRestricted) {
    allBadges.set('Restricted Product', {
      text: 'Restricted Product',
      icon: <BanIcon />,
    });
  }

  if (minOrderQuantity && minOrderQuantity > 1) {
    allBadges.set('Minimum Order Quantity', {
      text: `Minimum Order Quantity - ${minOrderQuantity}`,
      icon: <LayersIcon />,
    });
  }

  if (isDiscountExcluded) {
    allBadges.set('Excluded from Discount', {
      text: 'Excluded from Discount',
      icon: <BanIcon />,
    });
  }

  if (roundels) {
    roundels.forEach((roundel) => {
      const name = roundel.name;

      switch (name) {
        case 'Clearance':
          allBadges.set('Clearance', {
            text: roundel.text ?? 'Clearance',
            icon: <BadgePercentIcon />,
          });
          break;
        case 'Sale':
          allBadges.set('Sale', {
            text: roundel.text ?? 'Sale',
            icon: <BadgePercentIcon />,
          });
          break;
        case 'Reduced':
          allBadges.set('Reduced', {
            text: roundel.text ?? 'Reduced',
            icon: <BadgePercentIcon />,
          });
          break;
        case 'Buy More Save More':
          allBadges.set('Buy More Save More', {
            text: roundel.text ?? 'Buy More, Save More',
            icon: <BadgePercentIcon />,
          });
          break;
        case 'Best Seller':
          allBadges.set('Best Seller', {
            text: roundel.text ?? 'Best Seller',
            icon: <StarIcon />,
          });
          break;
        case 'New':
          allBadges.set('New', {
            text: roundel.text ?? 'New',
            icon: <Sparkle />,
          });
          break;
        case 'Exclusive':
          allBadges.set('Exclusive', {
            text: roundel.text ?? 'Exclusive',
            icon: <SparkleIcon />,
          });
          break;
        case 'Eco':
          allBadges.set('Eco Friendly', {
            text: 'Sustainable',
            icon: <LeafIcon />,
          });
          break;
        default:
          break;
      }
    });
  }

  if (allBadges.size === 0) {
    return null;
  }

  const calculatedLimit = limit ?? allBadges.size;

  const sortedBadges: ProductTagProps[] = [];

  const hasContractPrice = allBadges.has('Your Contract Price');

  for (const key of priorityOrder) {
    if (key === 'Excluded from Discount' && hasContractPrice) {
      continue; // Skip "Excluded from Discount" if "Your Contract Price" is present
    }

    if (allBadges.has(key)) {
      sortedBadges.push(allBadges.get(key)!);
    }
  }

  if (sortedBadges.length === 0) return null;

  return (
    <ul className="flex flex-wrap gap-2">
      {sortedBadges.slice(0, calculatedLimit).map((badge, index) => (
        <li key={index}>
          <ProductTag
            className={cn({
              'text-base': variant === 'pdp',
              'text-sm leading-6': variant === 'basket',
            })}
            {...badge}
          />
        </li>
      ))}
    </ul>
  );
}

type ProductTagProps = {
  text: string;
  icon?: React.ReactElement<SVGElement>;
  className?: string;
};

export function ProductTag({ text, className, icon }: ProductTagProps) {
  const processedIcon = icon
    ? React.cloneElement(icon, {
        className: 'product-tag__icon size-5 shrink-0 inline-flex',
      })
    : null;

  return (
    <div
      className={cn(
        'product-tag product-tag--' + getTagGroup(text),
        'inline-flex items-center gap-1 rounded-pill box-decoration-clone px-2 py-1 text-base font-medium leading-7',
        className,
      )}
    >
      {processedIcon} <span className="product-tag__text">{text}</span>
    </div>
  );
}

function getTagGroup(roundelName: string) {
  if (roundelName.toLowerCase().includes('minimum order quantity')) {
    return 'group-restrictions';
  }

  if (roundelName.toLowerCase().includes('excluded from discount')) {
    return 'group-savings';
  }

  switch (roundelName.toLowerCase()) {
    case 'new':
    case 'best seller':
    case 'exclusive':
      return 'group-highlights';

    case 'sustainable':
    case 'eco friendly':
      return 'group-other';

    case 'buy more save more':
    case 'clearance':
    case 'your price':
    case 'sale':
    case 'reduced':
      return 'group-savings';
    case 'restricted product':
    case 'your contract price':
      return 'group-restrictions';
    default:
      return '';
  }
}
